import React, { useState, useEffect, useCallback } from 'react';
import { Settings, FileText, ChevronRight, BookOpen, ChevronDown, Plus, X } from 'lucide-react';
import useEhCompacto from '../../../utils/useEhCompacto';
import ConservacaoVersoes from './ConservacaoVersoes';
import ConservacaoInspecao from './ConservacaoInspecao';
import ConservacaoHistorico from './ConservacaoHistorico';
import { perfilNoModulo } from '../../../utils/perfisDerivados';

// =====================================================================
// Sub-hub "Programa Anual de Conservação Predial" (Módulo Manutenção).
// A tela inicial é "Programas gravados" (ConservacaoHistorico, a tabela
// dinâmica Loja › Ano › Programa). Os botões de ação vivem no cabeçalho dela:
//   - "Nova Inspeção" → pop-up com os checklists publicados + o manual.
//   - "Gerenciar versões" → só Manutenção/Admin/Diretoria.
// Espelha o CertificacaoForms.js (padrão do módulo).
// =====================================================================

const API = '/api/manutencao/conservacao';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const ConservacaoForms = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const perfil = perfilNoModulo(user?.perfil, 'manutencao') || '';
  const podeGerenciar = ['Admin', 'Manutencao', 'Diretoria'].includes(perfil);

  const [tela, setTela] = useState('lista');        // lista | inspecionar | versoes
  const [templateSel, setTemplateSel] = useState(null);
  const [programaSel, setProgramaSel] = useState(null); // id do programa a retomar (Rascunho) ou null = novo
  const [modalNovo, setModalNovo] = useState(false);
  const [versoes, setVersoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  // Carrega os checklists publicados só quando o pop-up abre. Gerente (só-consulta)
  // não vê o botão "Nova Inspeção", então nunca dispara esta chamada (403).
  const carregar = useCallback(async () => {
    setLoading(true); setErro('');
    try {
      const r = await fetch(`${API}/versoes-publicadas`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setVersoes(await r.json());
    } catch (e) {
      setErro('Erro ao carregar os checklists publicados.');
    } finally {
      setLoading(false);
    }
  }, []);

  const abrirNovo = () => { setModalNovo(true); carregar(); };
  // Escolheu um checklist no pop-up → fecha o modal e abre a inspeção (nova).
  const escolherTemplate = (v) => {
    setTemplateSel(v); setProgramaSel(null); setModalNovo(false); setTela('inspecionar');
  };
  // Retomar um Rascunho a partir da tabela (leva o ID_TEMPLATE do programa).
  const continuarRascunho = (p) => {
    setTemplateSel({ ID: p.ID_TEMPLATE }); setProgramaSel(p.ID); setTela('inspecionar');
  };
  const voltarParaLista = () => { setTela('lista'); setTemplateSel(null); setProgramaSel(null); };

  if (tela === 'inspecionar' && templateSel) {
    return <ConservacaoInspecao idTemplate={templateSel.ID} idProgramaExistente={programaSel} onVoltar={voltarParaLista} />;
  }
  if (tela === 'versoes') return <ConservacaoVersoes onVoltar={() => setTela('lista')} />;

  // Botões de ação do cabeçalho da tela inicial (só quem gerencia).
  const acoes = podeGerenciar ? (
    <>
      <button onClick={abrirNovo} style={btnPrimario}>
        <Plus size={16} /> Nova Inspeção
      </button>
      <button onClick={() => setTela('versoes')} style={btn('#eef2ff', '#4338ca', '#c7d2fe')}>
        <Settings size={16} /> Gerenciar versões
      </button>
    </>
  ) : null;

  // Tela inicial: a própria consulta (recortada por loja no backend para o Gerente).
  // A navegação para fora do sub-hub é a barra de abas do módulo Manutenção — por
  // isso a lista não tem mais botão "Voltar" próprio.
  return (
    <>
      <ConservacaoHistorico acoesExtras={acoes} onContinuarRascunho={continuarRascunho} podeGerenciar={podeGerenciar} />
      {modalNovo && (
        <ModalNovaInspecao
          versoes={versoes} loading={loading} erro={erro}
          onEscolher={escolherTemplate} onFechar={() => setModalNovo(false)}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------
// Pop-up "Nova Inspeção": os checklists publicados (clicar = iniciar a
// inspeção) + o manual "como funciona" recolhível. Molde do
// ModalNovoDiagnostico da Certificação.
// ---------------------------------------------------------------------
const ModalNovaInspecao = ({ versoes, loading, erro, onEscolher, onFechar }) => {
  const compacto = useEhCompacto();
  const [manualAberto, setManualAberto] = useState(false);

  // Esc fecha o modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  return (
    <div
      onClick={onFechar}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: compacto ? 10 : 24, overflowY: 'auto',
      }}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720, marginTop: compacto ? 8 : 24,
          boxShadow: '0 20px 50px rgba(15,23,42,.25)', overflow: 'hidden',
        }}>
        {/* Cabeçalho do modal */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: compacto ? '14px 16px' : '18px 22px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ padding: 10, background: '#e0e7ff', borderRadius: 10 }}><Plus size={20} color="#4338ca" /></div>
          <div style={{ flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Nova Inspeção</h3>
            <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>Escolha o checklist publicado para iniciar a inspeção da unidade.</p>
          </div>
          <button onClick={onFechar} title="Fechar" style={{
            display: 'inline-flex', padding: 8, background: '#f1f5f9', color: '#475569',
            border: 'none', borderRadius: 8, cursor: 'pointer',
          }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: compacto ? 14 : 22 }}>
          {/* Manual recolhível — como funciona a conservação predial */}
          <div style={{ border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
            <button onClick={() => setManualAberto((v) => !v)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
              background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 800, color: '#4338ca', fontSize: 14,
            }}>
              <BookOpen size={18} /> Como funciona a conservação predial (passo a passo)
              <ChevronDown size={18} style={{ marginLeft: 'auto', transform: manualAberto ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
            </button>
            {manualAberto && (
              <div style={{ padding: '0 18px 16px', fontSize: 13, color: '#334155', lineHeight: 1.65 }}>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  <li><strong>Monte o checklist</strong> — em <strong>Gerenciar versões → Nova versão</strong>: crie as <strong>áreas</strong> na ordem do roteiro físico da inspeção (fachada → salão → cozinha → áreas técnicas) e os <strong>itens</strong> de cada uma. Marque “aceita N/A” nos itens que podem não existir na unidade (playground, cancela, guarita). Por fim, <strong>publique</strong>.</li>
                  <li style={{ marginTop: 6 }}><strong>Faça a inspeção (por exceção)</strong> — na lista abaixo, clique no checklist publicado e preencha os dados da unidade. Depois, <strong>marque as áreas em que você entrou</strong> e registre só as <strong>não conformidades</strong> (o que precisa de reparo): item, serviço a executar e foto. O que você olhou e está ok conta como conforme automaticamente — <strong>não é preciso classificar item por item</strong>. O <strong>ICP</strong> e a <strong>cobertura</strong> aparecem ao vivo.</li>
                  <li style={{ marginTop: 6 }}><strong>Não conformidade fora da lista</strong> — dentro de uma área visitada, escolha <strong>“Outro (fora do checklist)”</strong> ao registrar a não conformidade. Ela também gera Ordem de Serviço.</li>
                  <li style={{ marginTop: 6 }}><strong>Conclua e certifique</strong> — o servidor calcula o ICP e gera as Ordens de Serviço. Depois de executá-las, o gerente e o consultor validam e o programa vira <strong>Certificado</strong> (com o Relatório Técnico em PDF).</li>
                </ol>
                <div style={{ marginTop: 10, padding: 10, background: '#fff', border: '1px solid #c7d2fe', borderRadius: 8 }}>
                  <strong style={{ color: '#4338ca' }}>Como o ICP e a cobertura são calculados</strong><br />
                  <strong>ICP</strong> = itens sem não conformidade ÷ itens olhados. A régua é binária: toda não conformidade é uma pendência (não há meio ponto).
                  Classificação: ≥90% Excelente · 80–89% Muito Bom · 65–79% Bom · 50–64% Regular · &lt;50% Necessita Intervenção.<br />
                  <strong>Cobertura</strong> = itens das áreas visitadas ÷ total do checklist. Ela anda ao lado do ICP porque 89% olhando 3 áreas
                  não é a mesma informação que 89% olhando o prédio inteiro. Área que você <strong>não marcou como visitada</strong> fica fora das duas contas.<br />
                  Não conformidades fora do checklist entram no ICP colapsadas por lugar (5 no mesmo lugar contam 1) — inspecionar com rigor não vira desvantagem.
                </div>
              </div>
            )}
          </div>

          {erro && <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 16 }}>{erro}</div>}

          {loading ? <p style={{ color: '#64748b' }}>Carregando…</p> : versoes.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', background: '#f8fafc', borderRadius: 12, color: '#64748b' }}>
              Nenhum checklist publicado. Use “Gerenciar versões” para publicar um.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {versoes.map((v) => (
                <div key={v.ID}
                  onClick={() => onEscolher(v)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 16, padding: 18, background: '#fff',
                    border: '1px solid #e2e8f0', borderRadius: 12, cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#a5b4fc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; }}>
                  <div style={{ padding: 12, background: '#e0e7ff', borderRadius: 10 }}><FileText size={22} color="#4338ca" /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#0f172a' }}>{v.NOME_VERSAO}</div>
                    <div style={{ marginTop: 4, color: '#64748b', fontSize: 14 }}>
                      {v.QTD_ITENS} itens em {v.QTD_AREAS} área(s){v.DESCRICAO ? ` · ${v.DESCRICAO}` : ''}
                    </div>
                  </div>
                  <ChevronRight size={20} color="#cbd5e1" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const btn = (bg, color, border) => ({
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: bg, color, border: `1px solid ${border}`, borderRadius: 8, fontWeight: 700, cursor: 'pointer',
});

// Botão primário "Nova Inspeção" — cheio, ação de destaque.
const btnPrimario = {
  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 16px',
  backgroundColor: '#4f46e5', color: '#fff', border: '1px solid #4f46e5', borderRadius: 8, fontWeight: 800, cursor: 'pointer',
};

export default ConservacaoForms;
